import type { ErrorEvent, EventHint } from "@sentry/nextjs";

import { TOAST_CONSOLE_PREFIX } from "./toastBridgeMarker";

/**
 * Shared Sentry configuration used by both the client
 * (`instrumentation-client.ts`) and the Next.js server
 * (`sentry.server.config.ts`). Centralizing here prevents drift between
 * the two runtimes — when we add a new ignored pattern or a new PII field
 * to strip, we update one place and both runtimes pick it up.
 */

/**
 * Error messages we never want as Sentry issues.
 *
 * Server-only events will never match the browser-specific patterns
 * (Telegram, Firefox iOS, Safari) but keeping the list symmetric across
 * runtimes makes the configs identical and removes a class of "I forgot
 * to update one of the two" bugs.
 */
export const SHARED_IGNORE_ERRORS: (string | RegExp)[] = [
  // Telegram in-app browser SDK calls postEvent on hosts that don't expose it
  /Error invoking postEvent: Method not found/,
  // Firefox iOS reader-mode polyfill races with first paint
  /window\.__firefox__/,
  /__firefox__ is not defined/,
  // Safari aborted media / I-O reads (camera permission denials, file picker cancels)
  /NotReadableError: The I\/O read operation failed/,
  // Opaque iOS Safari rejections — augmented separately as warning-level
  /Non-Error promise rejection captured with value: null/,
  /Non-Error promise rejection captured with value: undefined/,
  // Benign ResizeObserver loop notification
  /ResizeObserver loop/,
  // Batch-exporter flush race (`_currentExport` nulled by an in-flight export's
  // .then between the null-check and the await in BatchLogRecordProcessorBase
  // ._flushAll). Two engine phrasings of the identical bug — V8 (Chrome/Node)
  // and WebKit (Safari/DuckDuckGo/iOS). The WebKit wording slipped past the V8
  // regex and surfaced separately as Sentry CUSTOMER-UI-3X; keep both so neither
  // engine re-opens it. (The pagehide `forceFlush().catch()` in lib/otel/logger
  // .ts stops it at source; these are belt-and-braces for any other caller.)
  /Cannot read properties of null \(reading 'exportCompleted'\)/,
  /null is not an object \(evaluating 'this\._currentExport\.exportCompleted'\)/,
  // Wallet browser-extension noise. MetaMask (and other wallets) inject an
  // `inpage.js` provider shim into the page and throw when the user has the
  // extension installed but it can't connect. We ship no wallet-connect
  // feature, so these are never our bugs — drop by message as well as by
  // source URL (see SHARED_DENY_URLS below). Sentry CUSTOMER-UI-3R.
  /Failed to connect to MetaMask/,
  /MetaMask extension not found/,
  // CefSharp bound-object bridge error. A .NET host embedding Chromium
  // (Microsoft's mail link scanner — the CUSTOMER-UI-7H event IP is in the
  // MSFT netblock; Sentry attributes the `simulateEvent` phrasing to the
  // Instagram webview) calls a bound method the page no longer has and rejects
  // with its own string. Nothing in our bundle produces it. The SDK's
  // DEFAULT_IGNORE_ERRORS covers only `MethodName:simulateEvent`, so the
  // `update` phrasing reached the issue stream — and GlobalErrorListener never
  // consults SDK defaults, so both phrasings would still toast without this.
  // Unanchored on purpose: it must match the raw `event.reason` string AND the
  // SDK's "Non-Error promise rejection captured with value: …" wrapper.
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
  // Safari IndexedDB eviction while a connection is open. The only IndexedDB
  // user in the page is mixpanel-browser's session recorder (queue + registry
  // in `mixpanelBrowserDb`). When the user clears site data or WebKit evicts
  // storage mid-session, the queue's `removeItem` rejects with this
  // DOMException and mixpanel's own `resetRecording` (idle-timeout timer)
  // floats it — a vendor path we cannot catch. Our own call sites are caught
  // at source (lib/swallowRecorderTeardown.ts); this entry covers the rest.
  // Trade-off, stated on purpose: this list also gates `logError`, so the
  // class becomes unobservable everywhere (toast, Mixpanel "Error Logged",
  // Sentry) — accepted for a one-user browser-teardown artefact. If we ever
  // add first-party IndexedDB (offline queue, draft cache), its user-delete
  // aborts would be swallowed here too: revisit then. Sentry CUSTOMER-UI-7J
  // (2026-09-17, Safari 26 on /register). No upstream fix in 2.74–2.83.
  /Database deleted by request of the user/,
];

/**
 * True when a global `error` / `unhandledrejection` message is benign noise we
 * deliberately suppress from BOTH the user-facing toast AND `logError` (console
 * + Sentry). The canonical example is the ResizeObserver loop notification,
 * which the spec fires on legitimate layout reflows when it can't deliver all
 * observations in one frame — nothing is actually broken.
 *
 * Reuses the string/regex entries of SHARED_IGNORE_ERRORS so the
 * GlobalErrorListener suppression list can never drift from Sentry's
 * `ignoreErrors`: Sentry's `eventFilters` integration already drops these from
 * the issue stream, but GlobalErrorListener never consults `ignoreErrors`, so it
 * would otherwise still toast + log. This is that parallel guard.
 *
 * Pure (string in → boolean out) so the Next.js *server* Sentry config can also
 * import this module without dragging in browser globals.
 */
export function isIgnorableGlobalError(message: unknown): boolean {
  if (typeof message !== "string") return false;
  return SHARED_IGNORE_ERRORS.some((pattern) =>
    typeof pattern === "string"
      ? message.includes(pattern)
      : pattern.test(message)
  );
}

/**
 * True for the muted cross-origin "Script error." that browsers deliver with NO
 * error object: when a script from a different origin throws and CORS forbids
 * reading the detail, the spec strips the real message/stack and substitutes the
 * fixed string "Script error." (some engines append a period, some don't) with
 * `event.error === null`. It is unactionable third-party noise, and the user's
 * own action has typically already succeeded.
 *
 * `GlobalErrorListener` uses this to SUPPRESS the false-alarm "An unexpected
 * error occurred." toast while STILL calling `logError` — the resulting Mixpanel
 * "Error Logged" event is our only queryable trace of the class, because Sentry
 * drops "Script error." via its default inbound filter. That is exactly why
 * "Script error." is deliberately NOT added to SHARED_IGNORE_ERRORS: that list
 * also gates logError/Sentry, so adding it there would blind us to the class
 * entirely rather than just hiding the toast.
 *
 * The `error == null` guard is load-bearing: iOS WebKit delivers a REAL,
 * actionable error as `event.error === null` alongside a genuine message, so we
 * match ONLY when the message itself is the muted "Script error." string — a
 * real iOS error keeps toasting.
 *
 * Pure (message + error in → boolean out) so it can be unit-tested and imported
 * without browser globals.
 */
export function isMutedCrossOriginScriptError(
  message: unknown,
  error: unknown
): boolean {
  return (
    error == null &&
    typeof message === "string" &&
    /^Script error\.?$/.test(message.trim())
  );
}

/** Strip a trailing `:line:col` (or `:line`) so a `$`-anchored deny pattern matches. */
function stripStackPosition(url: string): string {
  return url.replace(/:\d+(?::\d+)?$/, "");
}

/**
 * URL of the frame that actually threw — the FIRST frame in the stack. Handles
 * both `    at fn (url:1:2)` (V8) and `fn@url:1:2` (WebKit/Firefox); the leading
 * `Name: message` line is skipped because it is not a frame.
 */
function topFrameUrl(stack: string): string | undefined {
  for (const rawLine of stack.split("\n")) {
    const line = rawLine.trim();
    if (!line.startsWith("at ") && !line.includes("@")) continue;
    const match = /([a-z][a-z0-9+.-]*:\/\/[^\s)]+)/i.exec(line);
    if (match?.[1]) return stripStackPosition(match[1]);
  }
  return undefined;
}

/**
 * True when the error was thrown BY a script that isn't ours — a browser
 * extension, or an injected wallet shim — judged by the source URL of its top
 * stack frame against SHARED_DENY_URLS.
 *
 * This is the missing twin of `isIgnorableGlobalError`. Sentry runs two filters:
 * `ignoreErrors` (by message) and `denyUrls` (by source), and `GlobalErrorListener`
 * consults neither directly — `isIgnorableGlobalError` is the parallel guard for
 * the first, and this is the parallel guard for the second. Without it, a
 * foreign error Sentry silently drops still reaches the user as an "An unexpected
 * error occurred." toast and still burns a Mixpanel "Error Logged" event.
 *
 * Real case (replay c8a1b05f, 2026-09-09): an extension patched
 * `XMLHttpRequest.onreadystatechange` and threw `Cannot read properties of
 * undefined (reading 'M_ID')` on every XHR completion — 32 logged errors and 15
 * toasts in 175 seconds, zero of them ours. Sentry recorded none of it
 * (`denyUrls` did its job), which is exactly how the asymmetry stayed invisible.
 *
 * TOP frame only, matching Sentry's `denyUrls` semantics: an error thrown by OUR
 * code that merely passes through an extension-patched API is still ours and must
 * still surface. Matching "the stack mentions an extension anywhere" would
 * swallow real bugs, since these extensions patch globals every page uses.
 *
 * The message is deliberately NOT part of the test — foreign errors carry
 * ordinary messages (`Cannot read properties of undefined …`) that a real app bug
 * could produce verbatim. Origin is the only safe discriminator.
 *
 * Pure (error + optional url in → boolean out) so it is unit-testable and the
 * server Sentry config can import it without browser globals.
 *
 * @param error   `PromiseRejectionEvent.reason`, or `ErrorEvent.error`.
 * @param sourceUrl `ErrorEvent.filename` — used when the error carries no stack
 *                  (iOS WebKit delivers `error === null` with detail on the event).
 */
export function isThirdPartyScriptError(
  error: unknown,
  sourceUrl?: unknown
): boolean {
  const stack = (error as Error | undefined)?.stack;
  const url =
    (typeof stack === "string" ? topFrameUrl(stack) : undefined) ??
    (typeof sourceUrl === "string" ? stripStackPosition(sourceUrl) : undefined);
  if (!url) return false;

  return SHARED_DENY_URLS.some((pattern) =>
    typeof pattern === "string" ? url.includes(pattern) : pattern.test(url)
  );
}

/**
 * Source-script URLs whose errors are never ours, dropped via Sentry's
 * `denyUrls` (handled by the default `eventFilters` integration — the same
 * one that powers `ignoreErrors`).
 *
 * Browser wallet extensions (MetaMask, Coinbase Wallet, Trust, Rabby, …) inject
 * an `inpage.js` EIP-1193 provider into the page's OWN origin, so its stack
 * frames appear as `app:///scripts/inpage.js` rather than a `chrome-extension://`
 * URL — `denyUrls` matches on the frame URL, so this catches the whole class
 * regardless of the exact error wording or extension brand. Our own bundles are
 * always hashed `_next/static/chunks/*.js`, so this can never suppress a real
 * app error. The extension-protocol patterns cover errors that DO surface with
 * their raw extension origin. Sentry CUSTOMER-UI-3R.
 */
export const SHARED_DENY_URLS: (string | RegExp)[] = [
  /\/inpage\.js$/i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
];

/**
 * Strip query strings and fragments from URLs before telemetry export. Query
 * params can contain names, tokens, OTPs, or registration state and are not
 * protected by session-replay text masking.
 */
export function sanitizeUrlForTelemetry(rawUrl: string): string {
  try {
    const isRelative = rawUrl.startsWith("/");
    const url = new URL(rawUrl, "https://customer-web.local");
    url.search = "";
    url.hash = "";
    return isRelative ? url.pathname : url.toString();
  } catch {
    return "[INVALID_URL]";
  }
}

function sanitizeExtraUrls(extra: ErrorEvent["extra"]): void {
  if (!extra) return;

  for (const key of ["location", "url", "href"]) {
    const value = extra[key];
    if (typeof value === "string") {
      extra[key] = sanitizeUrlForTelemetry(value);
    }
  }
}

/**
 * `RangeError: Maximum call stack size exceeded` — a synchronous stack
 * overflow. On iOS WebKit it arrives via `window.onerror` with no resolvable
 * stack (a single `undefined:line:col` frame), so Sentry's default grouping
 * lumps every such event together with the unrelated auth-redirect
 * CUSTOMER-UI-3T issue. Match on the message so we can split them out.
 */
const STACK_OVERFLOW_MESSAGE = /Maximum call stack size exceeded/i;

/**
 * Toast notifications fan out to `console.*` (Mixpanel replay console + OTel
 * Kibana log + a Sentry breadcrumb), but must NEVER become Sentry *issues* —
 * they're routine user-facing notices, not bugs, and a `[toast]` flood would
 * bury real errors. No `captureConsoleIntegration` is installed today, so these
 * only ever reach Sentry as breadcrumbs and never hit `beforeSend`. This guard
 * makes the invariant durable: if a captureConsole integration is ever added,
 * any toast-originated console event is dropped here at the source rather than
 * spamming the issue stream.
 */
function isToastConsoleEvent(event: ErrorEvent): boolean {
  if (event.logger !== "console") return false;
  const candidates = [event.message, event.exception?.values?.[0]?.value];
  return candidates.some(
    (text) =>
      typeof text === "string" &&
      text.trimStart().startsWith(TOAST_CONSOLE_PREFIX)
  );
}

/**
 * Strip PII before any event leaves the SDK:
 *  - response bodies that may include BFF-filtered customer data, under all
 *    three key shapes they arrive in (see the block below — two of them were
 *    unmatched until 2026-09)
 *  - Authorization / Cookie headers when capturing request context
 *  - query strings / fragments from captured URLs
 *
 * This is the LAST line of defence, not the only one. By the time an event
 * reaches here its payloads are opaque JSON strings, so anything that needs
 * field-level redaction rather than deletion must be redacted at the source —
 * `redux/services/sentryErrorCapture.ts` (via `lib/logError.ts`'s `redact`).
 *
 * Also re-groups + de-prioritises stack-overflow errors (see
 * STACK_OVERFLOW_MESSAGE).
 *
 * Both the client and server configs install this. Adding a new field to
 * strip should happen here exactly once.
 */
export function sharedBeforeSend(
  event: ErrorEvent,
  _hint: EventHint
): ErrorEvent | null {
  // Drop toast-originated console events at the source — they belong in
  // Mixpanel (replay console + "Toast Displayed" Activity event) and Kibana,
  // never in the Sentry issue stream. No-op until a captureConsole integration
  // exists; see isToastConsoleEvent above.
  if (isToastConsoleEvent(event)) return null;

  // Stack overflows are caught by GlobalErrorListener + the SDK's global
  // handler (React stays mounted) and self-heal, and their stack is
  // unrecoverable on WebKit. Give them a dedicated fingerprint so they stop
  // masquerading as the auth-redirect CUSTOMER-UI-3T, and downgrade to
  // `warning` so the group is visible for trend-watching without reading as a
  // fatal regression. See `.claude/docs/incident-log.md` (2026-06).
  const primaryException = event.exception?.values?.[0];
  if (
    primaryException &&
    STACK_OVERFLOW_MESSAGE.test(primaryException.value ?? "")
  ) {
    event.fingerprint = [
      "stack-overflow",
      primaryException.type ?? "RangeError",
    ];
    event.level = "warning";
    event.tags = { ...event.tags, "error.class": "stack-overflow" };
  }

  // `contexts.response.body` is the SDK's own HTTP-response context. The two
  // Title-Case keys are ours: `captureRtkQueryError` sets them via
  // `scope.setContext("Response Body" | "Request Body", …)`, which lands under
  // `event.contexts`, NOT under `event.extra`.
  //
  // This block used to check `event.contexts.response.body` and
  // `event.extra["Response Body"]` only — neither of which is a key this app
  // ever sets — so it was dead code, and the response body it claims to strip
  // went to Sentry untouched. The bodies are now redacted at the source too
  // (`sentryErrorCapture.ts`); this is the second line of defence, and it is
  // deliberately a delete rather than a redact because at this point the body
  // is an opaque JSON string with no key structure left to walk.
  if (event.contexts?.response && "body" in event.contexts.response) {
    delete (event.contexts.response as { body?: unknown }).body;
  }
  if (event.contexts) {
    delete event.contexts["Response Body"];
  }
  if (event.extra && "Response Body" in event.extra) {
    delete event.extra["Response Body"];
  }
  sanitizeExtraUrls(event.extra);
  if (event.request?.headers) {
    delete event.request.headers.Authorization;
    delete event.request.headers.Cookie;
  }
  if (event.request?.url) {
    event.request.url = sanitizeUrlForTelemetry(event.request.url);
  }
  if (event.transaction?.includes("?")) {
    event.transaction = sanitizeUrlForTelemetry(event.transaction);
  }
  return event;
}
